import { useMemo } from 'react';
import _ from 'lodash';
import { useFetchService } from '../../utils/fetchService';
import { httpRequest } from '../../utils/network.service';

export type MdmIntegrationStatusSource = 'system' | 'customer' | 'support';

/** Mirrors organization-service's per-integration status (MAT-4932). */
export interface MdmIntegrationStatus {
  mdmIntegrationId: number;
  name?: string;
  status: 'active' | 'paused';
  statusSource: MdmIntegrationStatusSource;
  statusChangedAt: string | null;
  lastSync?: string | null;
}

export type MdmIntegrationStatusAction = 'pause' | 'resume';

export const isPaused = (status?: MdmIntegrationStatus) => status?.status === 'paused';

export const isPausedBySupport = (status?: MdmIntegrationStatus) => isPaused(status) && status?.statusSource === 'support';

export const PAUSED_BY_SUPPORT_TEXT = 'Paused by Quokka Support';

export const statusText = (status: MdmIntegrationStatus) => {
  if (!isPaused(status)) return 'Active';
  return isPausedBySupport(status) ? PAUSED_BY_SUPPORT_TEXT : 'Paused by customer';
};

/** Statuses for the caller's own org, keyed by integration id. Only configured integrations are listed. */
export const useMdmIntegrationStatuses = (accessToken: string) => {
  const { data, mutate } = useFetchService<MdmIntegrationStatus[]>('/organizations/mdm-integrations/status', accessToken);
  const statusById = useMemo(() => _.keyBy<MdmIntegrationStatus>(data ?? [], 'mdmIntegrationId'), [data]);
  return { statusById, mutate };
};

/** POSTs a pause or resume and returns the server's error message on failure. */
export const changeMdmIntegrationStatus = async (basePath: string, action: MdmIntegrationStatusAction) => {
  const { data, error } = await httpRequest({ path: `${process.env.ORGANIZATION_SERVICE_URL}${basePath}/${action}`, method: 'POST' });
  return { error, message: (data as { error?: string } | null)?.error };
};
